import React, { useState, useEffect } from "react";
import { Col, Row, CardBody } from "reactstrap";

import Flatpickr from "react-flatpickr";
import { MandarinTraditional } from "flatpickr/dist/l10n/zh-tw";
import "./GlobalSearchFilter.css";
import Select from "react-select";

import { useAsyncDebounce } from "react-table";
import { useTranslation } from "react-i18next";

export default function GlobalFilter({
  globalFilter,
  setGlobalFilter,
  datePicker = true,
  selectFilterOptions,
  setFilterParams,
  filterParams,
}) {
  const { t, i18n } = useTranslation();
  const datePickerRef = React.useRef(null);
  // search value, can work for client-side search by default
  const [filterSearch, setFilterSearch] = React.useState(
    filterParams?.search || globalFilter || ""
  );
  const [filterStatus, setFilterStatus] = useState(filterParams?.status || "");
  const [filterDates, setFilterDates] = useState(
    [filterParams?.date?.start, filterParams?.date?.end].filter(Boolean)
  );

  // Sync internal state with external filterParams when they change
  // (setting the same value again does nothing)
  useEffect(() => {
    if (filterParams?.search !== undefined) {
      setFilterSearch(filterParams.search);
    }
  }, [filterParams?.search]);

  useEffect(() => {
    if (filterParams?.status !== undefined) {
      setFilterStatus(filterParams.status);
    }
  }, [filterParams?.status]);

  // Debounced function to apply all filters
  const debouncedApplyFilters = useAsyncDebounce((search, status, dates) => {
    const filters = {
      search: search || "",
      date: {
        start: dates[0],
        end: dates[1],
      },
      status: status,
    };
    setFilterParams(filters);
  }, 300);

  // Debounced search that also updates globalFilter for client-side filtering
  const onSearchChange = useAsyncDebounce((value) => {
    setGlobalFilter(value || undefined);
  }, 200);

  const handleSearchChange = (value) => {
    setFilterSearch(value);
    onSearchChange(value);
    debouncedApplyFilters(value, filterStatus, filterDates);
  };

  // Dates as "YYYY-MM-DD". In range mode flatpickr also fires after the
  // first click; wait for the end date (or a clear) before filtering.
  const handleDateRange = (dates) => {
    if (dates.length === 1) return;
    const pad = (n) => String(n).padStart(2, "0");
    const arrayDateRange = dates.map(
      (val) =>
        `${val.getFullYear()}-${pad(val.getMonth() + 1)}-${pad(val.getDate())}`
    );
    setFilterDates(arrayDateRange);

    // Apply filters immediately when date changes
    const filters = {
      search: filterSearch || "",
      date: {
        start: arrayDateRange[0],
        end: arrayDateRange[1],
      },
      status: filterStatus,
    };
    setFilterParams(filters);
  };

  const handleStatusChange = (selectedOption) => {
    const newStatus = selectedOption?.value || "";
    setFilterStatus(newStatus);

    // Apply filters immediately when status changes
    const filters = {
      search: filterSearch || "",
      date: {
        start: filterDates[0],
        end: filterDates[1],
      },
      status: newStatus,
    };
    setFilterParams(filters);
  };

  return (
    <React.Fragment>
      <CardBody className="border border-dashed border-end-0 border-start-0">
        <form>
          <Row className="g-3">
            {/* Search filter - 2/3 width */}
            <Col sm={datePicker ? 6 : 8}>
              <div className="search-box me-2 mb-2 d-inline-block col-12">
                <input
                  onChange={(e) => handleSearchChange(e.target.value)}
                  id="search-bar-0"
                  type="text"
                  className="form-control search /"
                  placeholder={t("table.searchPlaceholder")}
                  value={filterSearch || ""}
                />
                <i className="bx bx-search-alt search-icon"></i>
              </div>
            </Col>

            {/* Date Range & Select filters - 1/3 width */}
            {/* <DateAndStatusFilter
							datePicker={datePicker}
							selectFilterOptions={selectFilterOptions}
						/> */}

            <Col sm={datePicker ? 6 : 4}>
              <Row className="g-3">
                {datePicker && (
                  <Col>
                    <div className="position-relative">
                      <Flatpickr
                        ref={datePickerRef}
                        className="form-control bg-white pe-5"
                        placeholder={t("table.selectDateRange")}
                        options={{
                          mode: "range",
                          dateFormat: "Y-m-d",
                          maxDate: "today",
                          locale:
                            i18n.language === "en"
                              ? "default"
                              : MandarinTraditional,
                        }}
                        value={filterDates}
                        onChange={(dates) => handleDateRange(dates)}
                        // Closed after picking only one day: filter that day
                        // (start = end). Fires onChange with both dates.
                        onClose={(dates, _dateStr, instance) => {
                          if (dates.length === 1) {
                            instance.setDate([dates[0], dates[0]], true);
                          }
                        }}
                      />
                      {filterDates.length > 0 && (
                        // Same cross icon and colours as react-select's clear
                        // button on the status filter next to it.
                        <button
                          type="button"
                          className="filter-clear-btn"
                          aria-label={t("table.clearDateRange")}
                          onClick={() =>
                            datePickerRef.current?.flatpickr.clear()
                          }
                        >
                          <svg
                            height="20"
                            width="20"
                            viewBox="0 0 20 20"
                            aria-hidden="true"
                            focusable="false"
                          >
                            <path d="M14.348 14.849c-0.469 0.469-1.229 0.469-1.697 0l-2.651-3.030-2.651 3.029c-0.469 0.469-1.229 0.469-1.697 0-0.469-0.469-0.469-1.229 0-1.697l2.758-3.15-2.759-3.152c-0.469-0.469-0.469-1.228 0-1.697s1.228-0.469 1.697 0l2.652 3.031 2.651-3.031c0.469-0.469 1.228-0.469 1.697 0s0.469 1.229 0 1.697l-2.758 3.152 2.758 3.15c0.469 0.469 0.469 1.229 0 1.698z" />
                          </svg>
                        </button>
                      )}
                    </div>
                  </Col>
                )}
                {selectFilterOptions.length > 0 && (
                  <Col>
                    <div>
                      <Select
                        placeholder={t("table.selectStatus")}
                        onChange={handleStatusChange}
                        options={selectFilterOptions}
                        value={
                          selectFilterOptions
                            .flatMap((group) => group.options || [group])
                            .find((option) => option.value === filterStatus) ||
                          null
                        }
                        name="choices-single-default"
                        isClearable
                      />
                    </div>
                  </Col>
                )}
              </Row>
            </Col>
          </Row>
        </form>
      </CardBody>
    </React.Fragment>
  );
}
